import { useState } from "react";

import Chip from "../../../app/universalComponents/Chip";
import { formatOrderDate } from "../../../app/utils/formatting";
import { getOrderItems } from "../dashboardApi";

function OrderItems({ items, loading, error }) {
  if (loading) {
    return (
      <p role="status" className="py-2 text-sm text-gray-500">
        Loading order items...
      </p>
    );
  }

  if (error) {
    return (
      <p role="alert" className="py-2 text-sm text-red-600">
        {error}
      </p>
    );
  }

  if (!items?.length) {
    return (
      <p className="py-2 text-sm text-gray-500">
        No items were found for this order.
      </p>
    );
  }

  return (
    <div className="divide-y">
      {items.map((item) => (
        <div
          key={item.id}
          className="flex items-center justify-between gap-4 py-2 text-sm"
        >
          <div className="min-w-0">
            <p className="truncate font-medium">
              {item.name}
            </p>

            <p className="text-xs text-gray-500">
              {item.quantity} × € {item.price}
            </p>
          </div>

          <p className="shrink-0 font-medium">
            € {item.total}
          </p>
        </div>
      ))}
    </div>
  );
}

function ExpandButton({ isOpen, onClick, controls }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={isOpen}
      aria-controls={controls}
      aria-label={isOpen ? "Hide order items" : "Show order items"}
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm transition hover:bg-gray-100"
    >
      <svg
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
        className={`h-5 w-5 transition-transform duration-200 ${
          isOpen ? "rotate-180" : ""
        }`}
      >
        <path
          fillRule="evenodd"
          d="M5.22 7.22a.75.75 0 0 1 1.06 0L10 10.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 8.28a.75.75 0 0 1 0-1.06Z"
          clipRule="evenodd"
        />
      </svg>
    </button>
  );
}

export default function OrderRow({ order, eventId }) {
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [loadingItems, setLoadingItems] = useState(false);
  const [itemsError, setItemsError] = useState("");

  const mobileDetailsId = `mobile-order-items-${order.id}`;
  const desktopDetailsId = `desktop-order-items-${order.id}`;

  async function handleToggle() {
    if (isOpen) {
      setIsOpen(false);
      return;
    }

    setIsOpen(true);

    if (items !== null || loadingItems) {
      return;
    }

    try {
      setLoadingItems(true);
      setItemsError("");

      const orderItems = await getOrderItems(eventId, order.id);

      setItems(orderItems);
    } catch (error) {
      setItemsError("The order items could not be loaded.");
    } finally {
      setLoadingItems(false);
    }
  }

  return (
    <>
      {/* Mobile */}
      <tr className="border-b md:hidden">
        <td colSpan={5} className="min-w-0 p-0">
          <button
            type="button"
            onClick={handleToggle}
            aria-expanded={isOpen}
            aria-controls={mobileDetailsId}
            className="block w-full px-0 py-2 text-left transition hover:bg-gray-50 active:bg-gray-100"
          >
            <div className="flex min-w-0 items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold">
                  {order.name}
                </p>

                <p className="truncate text-sm text-gray-500">
                  {order.email}
                </p>
              </div>

              <Chip status={order.status} />
            </div>

            <div className="mt-1 flex items-center justify-between gap-3 text-sm">
              <time>
                {formatOrderDate(order.date)}
              </time>

              <span className="shrink-0 font-medium">
                € {order.total}
              </span>
            </div>
          </button>

          {isOpen && (
            <div
              id={mobileDetailsId}
              className="border-t bg-gray-50 px-3 py-2"
            >

              <OrderItems
                items={items}
                loading={loadingItems}
                error={itemsError}
              />
            </div>
          )}
        </td>
      </tr>

      {/* Desktop */}
      <tr className="hidden border-t md:table-row">
        <td className="py-1">
          {order.name}
        </td>

        <td className="py-1">
          {order.email}
        </td>

        <td className="py-1">
          {formatOrderDate(order.date)}
        </td>

        <td className="py-1">
          € {order.total}
        </td>

        <td className="py-1">
          <div className="flex items-center justify-between gap-2">
            <Chip status={order.status} />

            <ExpandButton
              isOpen={isOpen}
              onClick={handleToggle}
              controls={desktopDetailsId}
            />
          </div>
        </td>
      </tr>

      {isOpen && (
        <tr
          id={desktopDetailsId}
          className="hidden border-t bg-gray-50 md:table-row"
        >
          <td colSpan={5} className="px-4 py-3">
            <div className="ml-auto max-w-2xl">

              <OrderItems
                items={items}
                loading={loadingItems}
                error={itemsError}
              />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}